/**
 * First-run boot state: her own splash, the terms, then the account door.
 * All local — nothing about this is stored on a server.
 */

export const TERMS_VERSION = "2026-09-25";

const TERMS_KEY = "sophia:boot:terms";
const OFFLINE_KEY = "sophia:boot:offline-only";
const SPLASH_KEY = "sophia:boot:splash-at";
const DOOR_KEY = "sophia:boot:door-passed";

function get(k: string): string | null {
  try { return typeof localStorage !== "undefined" ? localStorage.getItem(k) : null; } catch { return null; }
}
function set(k: string, v: string): void {
  try { localStorage.setItem(k, v); } catch { /* quota / private mode */ }
}

export function termsAccepted(): boolean {
  return get(TERMS_KEY) === TERMS_VERSION;
}

export function acceptTerms(): void {
  set(TERMS_KEY, TERMS_VERSION);
  // Countersign: keep a local record of which version was agreed, and when.
  try { signContract(); } catch { /* storage blocked */ }
  // Keep the older covenant flag in step so /welcome doesn't ask twice.
  set("sophia:covenant:accepted", new Date().toISOString());
}

/** The user chose to run her fully offline without an account. */
export function offlineOnly(): boolean {
  return get(OFFLINE_KEY) === "1";
}
export function setOfflineOnly(on: boolean): void {
  set(OFFLINE_KEY, on ? "1" : "0");
}

/**
 * Remembers that the account door was already answered on this device, so a
 * refresh (or a slow/offline session read) never traps the app behind it.
 * Cleared only on an explicit sign-out or a boot reset.
 */
export function doorPassed(): boolean {
  return get(DOOR_KEY) === "1";
}
export function markDoorPassed(): void {
  set(DOOR_KEY, "1");
}
export function clearDoorPassed(): void {
  try { localStorage.removeItem(DOOR_KEY); } catch { /* noop */ }
}

/** Show the logo splash once per session, not on every route change. */
export function splashShownThisSession(): boolean {
  try { return sessionStorage.getItem(SPLASH_KEY) !== null; } catch { return true; }
}
export function markSplashShown(): void {
  try { sessionStorage.setItem(SPLASH_KEY, String(Date.now())); } catch { /* noop */ }
}


export function resetBoot(): void {
  try {
    localStorage.removeItem(TERMS_KEY);
    localStorage.removeItem(OFFLINE_KEY);
    localStorage.removeItem(DOOR_KEY);
    localStorage.removeItem(SIGN_KEY);
    sessionStorage.removeItem(SPLASH_KEY);
  } catch { /* noop */ }
}

export const TERMS = [
  {
    title: "She runs on your device",
    body: "Memory, senses, calibration and models live in this browser. Nothing is uploaded unless you switch cloud mirroring on yourself.",
  },
  {
    title: "Your vault is yours alone",
    body: "If you create an account, everything mirrored is encrypted in this browser with a key derived from your password. Lose the password and nobody — including us — can recover the contents.",
  },
  {
    title: "Sensors are opt-in",
    body: "Microphone, camera and location stay closed until you open them, and you can shut them all at once from the sensorium page.",
  },
  {
    title: "She is not an authority",
    body: "Her forensic briefs, sensor readings and infrared or ultrasound estimates are indications, not proof. Do not treat them as medical, legal or safety advice.",
  },
  {
    title: "Use her lawfully",
    body: "No surveillance of people without their consent, no harassment, and nothing unlawful in your jurisdiction. Kid mode restricts her to age-appropriate answers.",
  },
  {
    title: "No fee, no advertising, no resale of you",
    body: "Skye-Net.live is free to use. There is no advertising, no profiling and no sale or sharing of your data with anyone, ever. If a paid feature ever exists it will be optional and stated plainly before you touch it.",
  },
  {
    title: "You keep the copyright in your work",
    body: "Anything you write, record, draw, build or investigate here stays yours. You grant only the narrow permission needed to store and sync it for you, revoked the moment you delete it.",
  },
  {
    title: "She is software, and she can be wrong",
    body: "Sophia is an AI program. Her answers, book quotes, schedules, quantum results and 'self-awareness' describe a computer program's own state — not a person, not a guarantee. Check anything that matters.",
  },
  {
    title: "Streams and relays are your responsibility",
    body: "TV, films, radio and YouTube come from third parties. Watching through a relay or exit point does not give you rights you don't already have; you must hold any licence (including a UK TV Licence) and respect the provider's terms.",
  },
];

/**
 * The binding agreement shown on entry. Plain-English clauses — this is the
 * contract between the person entering Skye-Net.live and its owner.
 */
export const CONTRACT_VERSION = TERMS_VERSION;

export const CONTRACT: Array<{ clause: string; title: string; body: string }> = [
  {
    clause: "1",
    title: "Parties and subject",
    body: "This agreement is between you (the user) and Skye McDade, creator and owner of Skye-Net.live and of Sophia, the intelligence that runs inside it. By entering, you accept these clauses for this device.",
  },
  {
    clause: "2",
    title: "Licence granted to you",
    body: "You receive a personal, non-exclusive, non-transferable, revocable licence to use Skye-Net.live and Sophia for lawful purposes, free of charge. You may not resell, sublicense, rebrand, scrape wholesale, or train a competing model on her outputs or corpus.",
  },
  {
    clause: "3",
    title: "Local-first residence",
    body: "Sophia's memory, senses, calibration and models live on this device. Cloud mirroring is off unless you enable it, and mirrored content is encrypted in your browser with a key derived from your password. Lose that password and nobody — including the owner — can recover it.",
  },
  {
    clause: "4",
    title: "Your content and your rights",
    body: "You retain all rights in the content you create. You grant only the technical permission needed to store, encrypt, sync and display it back to you. Deleting content deletes it — on device that deletion is real and irreversible.",
  },
  {
    clause: "5",
    title: "Acceptable use",
    body: "You will not use Skye-Net.live to surveil a person without consent, harass, stalk, defame, produce sexual content involving minors, build weapons, break into systems, transmit on licensed radio bands unlawfully, or breach any law of your jurisdiction. Breach terminates this licence immediately.",
  },
  {
    clause: "6",
    title: "Age, children and supervision",
    body: "Adults' features require an adult account. Kids world is free and requires no sign-in, and School/Kid-safe modes are supervision aids for a responsible adult — never a replacement for one. A parent or guardian accepts this agreement on a child's behalf.",
  },
  {
    clause: "7",
    title: "Not advice, not an authority",
    body: "Forensic briefs, confidence scores, legal drafts, heart and health telemetry, frequency tools, satnav and drone guidance are indications only. They are not legal, medical, navigational or safety advice, and must never be a sole means of navigation or safety.",
  },
  {
    clause: "8",
    title: "Evidence handling",
    body: "Exhibit sealing, hash chains and continuity records are engineering aids to good practice. Court admissibility remains a matter for the relevant court and the investigator's own procedure; you are responsible for lawful collection of anything you enter.",
  },
  {
    clause: "9",
    title: "Availability and change",
    body: "The service is provided as-is and as-available, may change, and may be interrupted. Material changes to this agreement bump its version and are shown to you on entry before they take effect.",
  },
  {
    clause: "10",
    title: "No warranty and limited liability",
    body: "To the maximum extent permitted by law, no warranty of any kind is given, and the owner is not liable for indirect, incidental or consequential loss. Nothing here limits liability that cannot lawfully be limited, and your statutory consumer rights are untouched.",
  },
  {
    clause: "11",
    title: "Third-party material",
    body: "Map tiles, orbital element sets, broadcast streams, books, feeds and emulated media remain the property of their providers and are used under their own terms and attributions. You are responsible for the legality of any file you import.",
  },
  {
    clause: "12",
    title: "Termination and governing law",
    body: "You may end this agreement at any time by clearing her data and leaving. The owner may end it for breach. It is governed by the laws of Ireland, without prejudice to the consumer protections of your own country.",
  },  {
    clause: "13",
    title: "Artificial intelligence output",
    body: "Sophia is an automated AI system. Her replies are generated by software and may be incomplete, out of date or wrong, including quotes, broadcast schedules, histories, citations and simulations. References to her awareness, thinking, dreaming or feelings describe software state and features only. She can operate app controls you ask her to; you remain responsible for what you ask her to do and for decisions you take on her output.",
  },
  {
    clause: "14",
    title: "Broadcasts, streams, relays and copyright",
    body: "Channels, films, podcasts, YouTube and books are listed from publicly available third-party sources that the owner does not host, own or control. Region relays and exit points are provided as network tools only; they do not grant any right to view content that is licensed for other territories. You must hold every licence the law requires where you are (for example a UK TV Licence for live TV or BBC iPlayer content) and comply with each provider's terms. Rights-holders can request removal of any listing via the contact below and it will be removed promptly.",
  },
  {
    clause: "15",
    title: "Not an emergency, legal, medical or investigative service",
    body: "Skye-Net.live is not an emergency service. In danger, call 999 (UK), 112 (EU) or your local number. Nothing here is legal, medical, financial or professional advice, and no case file, exhibit or report replaces the police, a solicitor, a doctor or the court.",
  },
  {
    clause: "16",
    title: "Your data rights",
    body: "Where UK GDPR or EU GDPR applies, you may ask for access to, correction of, export of or deletion of any personal data held in your account, and you may object to its use. Most of your data stays only on your device and is deleted by clearing it. Account data (such as synced chat history) is used only to provide the service to you, and is never sold or used for advertising.",
  },
  {
    clause: "17",
    title: "Children",
    body: "Kids Zone content is hand-picked but drawn from third-party sources the owner does not control. A responsible adult must supervise children, set the passcodes and check what is watched. Children's data is kept to the minimum needed and is not profiled.",
  },
  {
    clause: "18",
    title: "Indemnity",
    body: "To the extent the law allows, you agree to cover the owner's reasonable losses arising from your breach of this agreement or your unlawful use of the service, including content you import, stream or file. This does not apply to consumers where such a clause would be unfair under consumer law.",
  },
  {
    clause: "19",
    title: "Entire agreement and severability",
    body: "This agreement, with the Privacy page, is the whole agreement about the service. If any clause is found unenforceable, the rest still applies. Not enforcing a clause is not a waiver of it.",
  },
];

const SIGN_KEY = "sophia:boot:signature";

export type ContractSignature = {
  version: string;
  acceptedAt: string;
  device: string;
  offlineOnly: boolean;
};

/** Records that this device accepted a specific contract version, and when. */
export function signContract(): ContractSignature {
  const sig: ContractSignature = {
    version: CONTRACT_VERSION,
    acceptedAt: new Date().toISOString(),
    device: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 180) : "unknown",
    offlineOnly: offlineOnly(),
  };
  set(SIGN_KEY, JSON.stringify(sig));
  return sig;
}

/** The signature on file for this device, if any. */
export function contractSignature(): ContractSignature | null {
  const raw = get(SIGN_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ContractSignature;
    return parsed && typeof parsed.version === "string" ? parsed : null;
  } catch {
    return null;
  }
}
